import { useSyncExternalStore } from "react";
import { GLOBAL_CURRENCY } from "constants/appConstants";

// Display-only currency conversion. The backend keeps pricing the store in its
// own currency (e.g. ZAR); here we only convert what the visitor *sees* using
// free daily rates from frankfurter.dev. Checkout and order records are never
// converted — the customer is charged in the store currency.

const RATES_URL = "https://api.frankfurter.dev/v2/rates";
const RATE_TTL_MS = 24 * 60 * 60 * 1000; // rates only change once a day
const LS_RATE_PREFIX = "fx_rate_";

const SYMBOL_TO_CODE = {
  R: "ZAR", $: "USD", "£": "GBP", "€": "EUR", "₹": "INR", "¥": "JPY",
  "A$": "AUD", "C$": "CAD", "NZ$": "NZD", "₦": "NGN", KSh: "KES", P: "BWP",
  "د.إ": "AED", "₩": "KRW", "₱": "PHP", "฿": "THB", "S$": "SGD",
};

// Region (from navigator.language, e.g. en-GB -> GB) to currency. Covers every ISO country.
// A currency Frankfurter doesn't publish just falls back to the store currency.
const REGION_TO_CODE = {
  AD: "EUR", AE: "AED", AF: "AFN", AG: "XCD", AI: "XCD", AL: "ALL", AM: "AMD",
  AO: "AOA", AR: "ARS", AS: "USD", AT: "EUR", AU: "AUD", AW: "AWG", AX: "EUR",
  AZ: "AZN", BA: "BAM", BB: "BBD", BD: "BDT", BE: "EUR", BF: "XOF", BG: "EUR",
  BH: "BHD", BI: "BIF", BJ: "XOF", BL: "EUR", BM: "BMD", BN: "BND", BO: "BOB",
  BQ: "USD", BR: "BRL", BS: "BSD", BT: "BTN", BV: "NOK", BW: "BWP", BY: "BYN",
  BZ: "BZD", CA: "CAD", CC: "AUD", CD: "CDF", CF: "XAF", CG: "XAF", CH: "CHF",
  CI: "XOF", CK: "NZD", CL: "CLP", CM: "XAF", CN: "CNY", CO: "COP", CR: "CRC",
  CU: "CUP", CV: "CVE", CW: "ANG", CX: "AUD", CY: "EUR", CZ: "CZK", DE: "EUR",
  DJ: "DJF", DK: "DKK", DM: "XCD", DO: "DOP", DZ: "DZD", EC: "USD", EE: "EUR",
  EG: "EGP", EH: "MAD", ER: "ERN", ES: "EUR", ET: "ETB", FI: "EUR", FJ: "FJD",
  FK: "FKP", FM: "USD", FO: "DKK", FR: "EUR", GA: "XAF", GB: "GBP", GD: "XCD",
  GE: "GEL", GF: "EUR", GG: "GBP", GH: "GHS", GI: "GIP", GL: "DKK", GM: "GMD",
  GN: "GNF", GP: "EUR", GQ: "XAF", GR: "EUR", GT: "GTQ", GU: "USD", GW: "XOF",
  GY: "GYD", HK: "HKD", HM: "AUD", HN: "HNL", HR: "EUR", HT: "HTG", HU: "HUF",
  ID: "IDR", IE: "EUR", IL: "ILS", IM: "GBP", IN: "INR", IO: "USD", IQ: "IQD",
  IR: "IRR", IS: "ISK", IT: "EUR", JE: "GBP", JM: "JMD", JO: "JOD", JP: "JPY",
  KE: "KES", KG: "KGS", KH: "KHR", KI: "AUD", KM: "KMF", KN: "XCD", KP: "KPW",
  KR: "KRW", KW: "KWD", KY: "KYD", KZ: "KZT", LA: "LAK", LB: "LBP", LC: "XCD",
  LI: "CHF", LK: "LKR", LR: "LRD", LS: "LSL", LT: "EUR", LU: "EUR", LV: "EUR",
  LY: "LYD", MA: "MAD", MC: "EUR", MD: "MDL", ME: "EUR", MF: "EUR", MG: "MGA",
  MH: "USD", MK: "MKD", ML: "XOF", MM: "MMK", MN: "MNT", MO: "MOP", MP: "USD",
  MQ: "EUR", MR: "MRU", MS: "XCD", MT: "EUR", MU: "MUR", MV: "MVR", MW: "MWK",
  MX: "MXN", MY: "MYR", MZ: "MZN", NA: "NAD", NC: "XPF", NE: "XOF", NF: "AUD",
  NG: "NGN", NI: "NIO", NL: "EUR", NO: "NOK", NP: "NPR", NR: "AUD", NU: "NZD",
  NZ: "NZD", OM: "OMR", PA: "PAB", PE: "PEN", PF: "XPF", PG: "PGK", PH: "PHP",
  PK: "PKR", PL: "PLN", PM: "EUR", PN: "NZD", PR: "USD", PS: "ILS", PT: "EUR",
  PW: "USD", PY: "PYG", QA: "QAR", RE: "EUR", RO: "RON", RS: "RSD", RU: "RUB",
  RW: "RWF", SA: "SAR", SB: "SBD", SC: "SCR", SD: "SDG", SE: "SEK", SG: "SGD",
  SH: "SHP", SI: "EUR", SJ: "NOK", SK: "EUR", SL: "SLE", SM: "EUR", SN: "XOF",
  SO: "SOS", SR: "SRD", SS: "SSP", ST: "STN", SV: "USD", SX: "ANG", SY: "SYP",
  SZ: "SZL", TC: "USD", TD: "XAF", TF: "EUR", TG: "XOF", TH: "THB", TJ: "TJS",
  TK: "NZD", TL: "USD", TM: "TMT", TN: "TND", TO: "TOP", TR: "TRY", TT: "TTD",
  TV: "AUD", TW: "TWD", TZ: "TZS", UA: "UAH", UG: "UGX", UM: "USD", US: "USD",
  UY: "UYU", UZ: "UZS", VA: "EUR", VC: "XCD", VE: "VES", VG: "USD", VI: "USD",
  VN: "VND", VU: "VUV", WF: "XPF", WS: "WST", XK: "EUR", YE: "YER", YT: "EUR",
  ZA: "ZAR", ZM: "ZMW", ZW: "USD",
};

const safeGet = (key) => {
  try { return window.localStorage.getItem(key); } catch (e) { return null; }
};
const safeSet = (key, value) => {
  try { window.localStorage.setItem(key, value); } catch (e) { /* storage blocked */ }
};

let state = { base: GLOBAL_CURRENCY.code, target: null, country: null, rate: 1, version: 0, detected: false };
const listeners = new Set();
const emit = () => {
  state = { ...state, version: state.version + 1 };
  listeners.forEach((l) => l());
};

export const codeFromSymbol = (symbol) => SYMBOL_TO_CODE[symbol] || GLOBAL_CURRENCY.code;

// Fallback only: guess the country from the browser language (en-GB -> GB).
const regionFromLanguage = () => {
  try {
    return new Intl.Locale(navigator.language).maximize().region || null;
  } catch (e) {
    return null;
  }
};

const COUNTRY_SERVICES = [
  { url: "https://ipapi.co/json/", pick: (d) => (d?.error ? null : d?.country) }, // same service the phone input uses
  { url: "https://api.country.is/", pick: (d) => d?.country },
  { url: "https://ipwho.is/?fields=country_code,success", pick: (d) => (d?.success ? d.country_code : null) },
];
const COUNTRY_TTL_MS = 24 * 60 * 60 * 1000;
const LS_COUNTRY = "visitor_country";

const lookupCountry = async () => {
  for (const svc of COUNTRY_SERVICES) {
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 2500);
      const res = await fetch(svc.url, { signal: ctrl.signal });
      clearTimeout(timer);
      if (!res.ok) continue;
      const code = String(svc.pick(await res.json()) || "").toUpperCase();
      if (REGION_TO_CODE[code]) return code;
    } catch (e) { /* try the next service */ }
  }
  return null;
};

// Where the visitor actually is (IP), cached a day; browser language only if the lookup fails.
// A country picked by hand (manual) is kept until they pick another.
const detectCountry = async () => {
  let country = null;
  try {
    const cached = JSON.parse(safeGet(LS_COUNTRY));
    if (cached && REGION_TO_CODE[cached.country] && (cached.manual || Date.now() - cached.at < COUNTRY_TTL_MS)) {
      country = cached.country;
    }
  } catch (e) { /* ignore corrupt cache */ }
  if (!country) {
    country = await lookupCountry();
    if (country) safeSet(LS_COUNTRY, JSON.stringify({ country, at: Date.now() }));
  }
  const final = REGION_TO_CODE[country] ? country : regionFromLanguage();
  return REGION_TO_CODE[final] ? final : null;
};

const readCachedRate = (base, target) => {
  try {
    const cached = JSON.parse(safeGet(`${LS_RATE_PREFIX}${base}_${target}`));
    if (cached && cached.rate > 0) return cached;
  } catch (e) { /* ignore corrupt cache */ }
  return null;
};

const loadRate = async (base, target) => {
  const cached = readCachedRate(base, target);
  if (cached) {
    state = { ...state, rate: cached.rate };
    emit();
    if (Date.now() - cached.at < RATE_TTL_MS) return;
  }
  try {
    const res = await fetch(`${RATES_URL}?base=${base}&quotes=${target}`);
    if (!res.ok) return;
    const data = await res.json();
    const rate = Array.isArray(data) ? data[0]?.rate : null;
    if (!(rate > 0)) return;
    safeSet(`${LS_RATE_PREFIX}${base}_${target}`, JSON.stringify({ rate, at: Date.now() }));
    if (state.target === target && state.base === base) {
      state = { ...state, rate };
      emit();
    }
  } catch (e) {
    // Network/rates failure: keep showing store-currency prices.
  }
};

// Called once the store's own currency is known (from websiteInfo). The backend's
// currency_code is used when present; the symbol map is only a fallback.
export const initCurrency = async (storeSymbol, storeCode) => {
  const base = (storeCode || "").toUpperCase() || codeFromSymbol(storeSymbol);
  if (state.base === base && state.detected) return;
  state = { base, target: null, country: null, rate: 1, version: state.version, detected: true };
  const country = await detectCountry();
  const target = country ? REGION_TO_CODE[country] : null;
  if (state.base !== base) return; // store changed while we were looking up
  state = { ...state, country, target };
  if (!target || target === base) { emit(); return; }
  loadRate(base, target);
};

// Visitor picked a country by hand (header/footer dropdown): use its currency from now on.
export const setVisitorCountry = (code) => {
  const country = String(code || "").toUpperCase();
  if (!REGION_TO_CODE[country]) return;
  safeSet(LS_COUNTRY, JSON.stringify({ country, at: Date.now(), manual: true }));
  const target = REGION_TO_CODE[country];
  state = { ...state, country, target, rate: 1 };
  emit();
  if (target !== state.base) loadRate(state.base, target);
};

const LS_SUPPORTED = "fx_supported";
const SUPPORTED_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Currencies Frankfurter publishes (cached a week). Resolves null if unavailable.
export const loadSupportedCurrencies = async () => {
  try {
    const cached = JSON.parse(safeGet(LS_SUPPORTED));
    if (cached && Array.isArray(cached.codes) && Date.now() - cached.at < SUPPORTED_TTL_MS) return new Set(cached.codes);
  } catch (e) { /* ignore corrupt cache */ }
  try {
    const res = await fetch("https://api.frankfurter.dev/v2/currencies");
    if (!res.ok) return null;
    const codes = (await res.json()).map((c) => c.iso_code);
    safeSet(LS_SUPPORTED, JSON.stringify({ codes, at: Date.now() }));
    return new Set(codes);
  } catch (e) {
    return null;
  }
};

const symbolOf = (code) => {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: code, currencyDisplay: "narrowSymbol" })
      .formatToParts(0).find((p) => p.type === "currency")?.value || code;
  } catch (e) {
    return code;
  }
};

// Every country with its currency, for the picker. Pass the supported set to hide
// countries whose currency can't be converted.
export const getCountryOptions = (supported) => {
  let names = null;
  try { names = new Intl.DisplayNames(["en"], { type: "region" }); } catch (e) { /* no names */ }
  return Object.entries(REGION_TO_CODE)
    .filter(([, cur]) => cur === state.base || !supported || supported.has(cur))
    .map(([code, cur]) => ({ code, name: (names && names.of(code)) || code, currency: cur, symbol: symbolOf(cur) }))
    .filter((o) => o.name !== o.code)
    .sort((x, y) => x.name.localeCompare(y.name));
};

// True only when a conversion is actually active.
export const isConverting = () =>
  !!state.target && state.target !== state.base && state.rate !== 1;

export const getDisplayCurrency = () => (isConverting() ? state.target : state.base);

// Convert a store-currency amount for display. Returns { value, symbol } where
// symbol is null when no conversion applies (caller keeps its own symbol and
// precision). precision is the target currency's own decimal places.
export const convertForDisplay = (value) => {
  if (!isConverting()) return { value, symbol: null, precision: 2 };
  const converted = Number(value) * state.rate;
  let symbol = state.target;
  let precision = 2;
  try {
    const fmt = new Intl.NumberFormat(undefined, {
      style: "currency", currency: state.target, currencyDisplay: "narrowSymbol",
    });
    symbol = fmt.formatToParts(0).find((p) => p.type === "currency")?.value || state.target;
    precision = fmt.resolvedOptions().maximumFractionDigits; // e.g. JPY 0, KWD 3
  } catch (e) { /* fall back to code / 2 decimals */ }
  return { value: converted, symbol, precision };
};

const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
const getSnapshot = () => state;

// Components that print converted prices call this so they re-render when rates arrive.
export const useDisplayCurrency = () => useSyncExternalStore(subscribe, getSnapshot);
